/**
 * fiscal.js — Indian financial year and credit terms.
 *
 * DEFECTS IN v1 THAT THIS FIXES
 *  1. Invoice series were keyed on `new Date().getFullYear()` — the calendar
 *     year. Indian invoice series run on the financial year (1 April to
 *     31 March), so the numbering reset on 1 January, mid-year.
 *  2. The number was generated from *today* rather than from the invoice date,
 *     so backdating an invoice produced an out-of-sequence number.
 *  3. Due dates were computed as
 *        new Date().setDate(invoiceDate.getDate() + creditPeriod)
 *     which starts from today, takes only the day-of-month from the invoice
 *     date, and returns a NUMBER (epoch ms) rather than a Date. For a 28 Feb
 *     invoice with 5-day terms it produced an arbitrary date in the current
 *     month. With a blank credit period it produced NaN, and formatting threw.
 */

/** The financial year containing `date`, as { startYear, endYear, label, key }. */
export function financialYear(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  // April is month index 3.
  const startYear = d.getMonth() >= 3 ? year : year - 1;
  const endYear = startYear + 1;
  return {
    startYear,
    endYear,
    /** "2026-27" — what appears on the invoice. */
    label: `${startYear}-${String(endYear).slice(-2)}`,
    /** "2026-2027" — the Firestore document id for the counter. */
    key: `${startYear}-${endYear}`,
    start: new Date(startYear, 3, 1, 0, 0, 0, 0),
    end: new Date(endYear, 2, 31, 23, 59, 59, 999),
  };
}


/**
 * Add a credit period (in days) to the invoice date.
 * Returns a real Date, based on the invoice date, and falls back to the
 * invoice date itself when the credit period is missing or not a number.
 */
export function calculateDueDate(invoiceDate, creditPeriodDays) {
  const base = invoiceDate instanceof Date ? new Date(invoiceDate) : new Date(invoiceDate);
  if (Number.isNaN(base.getTime())) return null;

  const days = Number(creditPeriodDays);
  if (!Number.isFinite(days) || days <= 0) {
    return new Date(base.getFullYear(), base.getMonth(), base.getDate());
  }
  const due = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  due.setDate(due.getDate() + Math.trunc(days));
  return due;
}

/** Common named date ranges for list filters. All inclusive of both ends. */
export function dateRange(preset, reference = new Date()) {
  const now = new Date(reference);
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
  const fy = financialYear(now);

  switch (preset) {
    case 'today':
      return { from: startOfDay(now), to: endOfDay(now), label: 'Today' };
    case 'yesterday': {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      return { from: startOfDay(y), to: endOfDay(y), label: 'Yesterday' };
    }
    case 'last7': {
      const s = new Date(now);
      s.setDate(s.getDate() - 6);
      return { from: startOfDay(s), to: endOfDay(now), label: 'Last 7 Days' };
    }
    case 'last30': {
      const s = new Date(now);
      s.setDate(s.getDate() - 29);
      return { from: startOfDay(s), to: endOfDay(now), label: 'Last 30 Days' };
    }
    case 'thisMonth':
      return {
        from: new Date(now.getFullYear(), now.getMonth(), 1),
        to: endOfDay(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
        label: 'This Month',
      };
    case 'lastMonth':
      return {
        from: new Date(now.getFullYear(), now.getMonth() - 1, 1),
        to: endOfDay(new Date(now.getFullYear(), now.getMonth(), 0)),
        label: 'Last Month',
      };
    case 'thisQuarter': {
      const q = Math.floor(now.getMonth() / 3);
      return {
        from: new Date(now.getFullYear(), q * 3, 1),
        to: endOfDay(new Date(now.getFullYear(), q * 3 + 3, 0)),
        label: 'This Quarter',
      };
    }
    case 'thisFY':
      return { from: fy.start, to: fy.end, label: `FY ${fy.label}` };
    case 'lastFY': {
      const prev = financialYear(new Date(fy.startYear - 1, 6, 1));
      return { from: prev.start, to: prev.end, label: `FY ${prev.label}` };
    }
    case 'all':
    default:
      return { from: null, to: null, label: 'All Time' };
  }
}

/**
 * `FY 2026-27` — the label to show anywhere a financial year is named.
 *
 * Say the year, never "this year". An accountant reconciling two screens has to
 * know WHICH year is on the page, and "This Financial Year" is only meaningful
 * to someone who already knows today's date and the 1 April boundary. It also
 * goes wrong on a backdated document, where "this" is not the year on screen.
 */
export function financialYearLabel(date = new Date()) {
  return `FY ${financialYear(date).label}`;
}

/**
 * The date presets, with the two financial-year entries named by their actual
 * year rather than by "This" / "Last". Built from a reference date so the
 * labels are correct on either side of 1 April.
 */
export function datePresets(reference = new Date()) {
  const fy = financialYear(reference);
  const previous = financialYear(new Date(fy.startYear - 1, 6, 1));
  return [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last7', label: 'Last 7 Days' },
    { value: 'last30', label: 'Last 30 Days' },
    { value: 'thisMonth', label: 'This Month' },
    { value: 'lastMonth', label: 'Last Month' },
    { value: 'thisQuarter', label: 'This Quarter' },
    { value: 'thisFY', label: `FY ${fy.label}` },
    { value: 'lastFY', label: `FY ${previous.label}` },
  ];
}

/**
 * The presets as a plain array, for the many call sites that pass them straight
 * to a `<Select options=…>`. Computed once at module load: the only way it goes
 * stale is a browser tab left open across midnight on 31 March, and a reload
 * fixes that. Anything that must be right at render time should call
 * `datePresets()` instead.
 */
export const DATE_PRESETS = datePresets();
